"use client"

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useAuthToken } from "@/hooks/use-auth"

// API client configuration - empty string means same origin (Vite proxies /api to Express)
const API_BASE = ""

// ============================================
// Type Definitions
// ============================================

export type ChatMode = "kb" | "kb_templates" | "kb_templates_external"

export interface SourceCitation {
  documentId: string
  documentName: string
  chunkId: string
  excerpt: string
  score: number
}

export interface ArtefactCitation {
  type: string
  id: string
  name: string
  sectionPath?: string
  deepLink?: string
}

export interface Message {
  id: string
  role: "user" | "assistant"
  content: string
  timestamp: string
  citations?: Array<{
    documentId: string
    documentName: string
    content: string
    page?: number
  }>
  sourcesUsed?: SourceCitation[]
  artefactsUsed?: ArtefactCitation[]
}

export interface Conversation {
  id: string
  projectId: string
  title: string
  createdAt: string
  updatedAt?: string
  messageCount?: number
  lastMessage?: string
}

export interface ConversationDetail extends Conversation {
  messages: Message[]
}

export interface CreateConversationInput {
  title?: string
  initialMessage?: string
}

// ============================================
// API Response Types
// ============================================

interface PaginatedResponse<T> {
  success: boolean
  data: T[]
  meta: {
    pagination: {
      total: number
      page: number
      pageSize: number
      totalPages: number
    }
  }
}

interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
}

// ============================================
// Helper Functions
// ============================================

/**
 * Parse citations JSON string from persisted messages into sourcesUsed/artefactsUsed.
 * Backend stores citations as JSON string: { sources: [...], artefacts: [...] }
 */
function parseMessageCitations(message: Message & { citations?: unknown }): Message {
  // If already has parsed sourcesUsed/artefactsUsed, return as-is
  if (message.sourcesUsed || message.artefactsUsed) {
    return message
  }

  // Try to parse citations if it exists
  if (message.citations) {
    try {
      // citations might be a string (from DB) or already an object
      const citationsData =
        typeof message.citations === "string"
          ? JSON.parse(message.citations)
          : message.citations

      // Handle { sources: [...], artefacts: [...] } format from backend
      if (citationsData && typeof citationsData === "object") {
        const sources = citationsData.sources || citationsData.sourcesUsed || []
        const artefacts = citationsData.artefacts || citationsData.artefactsUsed || []

        return {
          ...message,
          sourcesUsed: sources.map((s: Record<string, unknown>) => ({
            documentId: s.documentId || s.document_id || "",
            documentName: s.documentName || s.document_name || "",
            chunkId: s.chunkId || s.chunk_id || "",
            excerpt: s.excerpt || s.content || "",
            score: s.score || 0,
          })),
          artefactsUsed: artefacts.map((a: Record<string, unknown>) => ({
            type: a.type || "",
            id: a.id || "",
            name: a.name || "",
            sectionPath: a.sectionPath || a.section_path,
            deepLink: a.deepLink || a.deep_link,
          })),
          citations: undefined, // Remove raw citations to avoid confusion
        }
      }
    } catch {
      // If parsing fails, return message without citations
      console.warn("Failed to parse message citations:", message.citations)
    }
  }

  return message
}

// ============================================
// Fetch helpers
// ============================================

async function fetchConversations(
  projectId: string,
  token: string | null,
  params?: { page?: number; pageSize?: number }
): Promise<{ conversations: Conversation[]; total: number }> {
  if (!token) return { conversations: [], total: 0 }

  const searchParams = new URLSearchParams()
  if (params?.page) searchParams.set("page", params.page.toString())
  if (params?.pageSize) searchParams.set("pageSize", params.pageSize.toString())

  const queryString = searchParams.toString()
  const response = await fetch(
    `${API_BASE}/api/projects/${projectId}/conversations${queryString ? `?${queryString}` : ""}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  )

  if (!response.ok) {
    if (response.status === 401 || response.status === 404) {
      return { conversations: [], total: 0 }
    }
    throw new Error("Failed to fetch conversations")
  }

  const data: PaginatedResponse<Conversation> = await response.json()
  return {
    conversations: data.data,
    total: data.meta.pagination.total,
  }
}

async function fetchConversation(
  projectId: string,
  conversationId: string,
  token: string | null
): Promise<ConversationDetail | null> {
  if (!token) return null

  const response = await fetch(
    `${API_BASE}/api/projects/${projectId}/conversations/${conversationId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  )

  if (!response.ok) {
    if (response.status === 404) return null
    throw new Error("Failed to fetch conversation")
  }

  const data: ApiResponse<ConversationDetail> = await response.json()

  // Parse citations in messages so sourcesUsed/artefactsUsed are available
  if (data.data?.messages) {
    data.data.messages = data.data.messages.map(parseMessageCitations)
  }

  return data.data
}

async function createConversation(
  projectId: string,
  input: CreateConversationInput,
  token: string | null
): Promise<Conversation> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(`${API_BASE}/api/projects/${projectId}/conversations`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.error?.message || error.message || "Failed to create conversation")
  }

  const data: ApiResponse<Conversation> = await response.json()
  return data.data
}

async function deleteConversation(
  projectId: string,
  conversationId: string,
  token: string | null
): Promise<void> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(
    `${API_BASE}/api/projects/${projectId}/conversations/${conversationId}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.error?.message || error.message || "Failed to delete conversation")
  }
}

// ============================================
// Hooks
// ============================================

export function useConversations(
  projectId: string,
  params?: { page?: number; pageSize?: number }
) {
  const token = useAuthToken()

  return useQuery({
    queryKey: ["conversations", projectId, params],
    queryFn: () => fetchConversations(projectId, token, params),
    enabled: !!projectId && !!token,
  })
}

export function useConversation(projectId: string, conversationId: string) {
  const token = useAuthToken()

  return useQuery({
    queryKey: ["conversations", projectId, conversationId],
    queryFn: () => fetchConversation(projectId, conversationId, token),
    enabled: !!projectId && !!conversationId && !!token,
  })
}

export function useCreateConversation(projectId: string) {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (input: CreateConversationInput) => createConversation(projectId, input, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["conversations", projectId] })
    },
  })
}

export function useDeleteConversation(projectId: string) {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (conversationId: string) => deleteConversation(projectId, conversationId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["conversations", projectId] })
    },
  })
}

// Hook for conversation history (used in chat panel)
export function useConversationHistory(projectId: string, conversationId: string | null) {
  const token = useAuthToken()

  return useQuery({
    queryKey: ["conversations", projectId, conversationId, "history"],
    queryFn: () =>
      conversationId ? fetchConversation(projectId, conversationId, token) : null,
    enabled: !!projectId && !!conversationId && !!token,
    refetchInterval: 5000, // Poll for new messages
  })
}

// Send message helper
async function sendMessage(
  projectId: string,
  conversationId: string,
  content: string,
  token: string | null,
  mode: ChatMode = "kb"
): Promise<{
  userMessage: Message
  assistantMessage: Message
}> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(
    `${API_BASE}/api/projects/${projectId}/conversations/${conversationId}/messages`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ content, mode }),
    }
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.error?.message || error.message || "Failed to send message")
  }

  const data: ApiResponse<{ userMessage: Message; assistantMessage: Message }> = await response.json()
  return data.data
}

// Hook for sending messages within a conversation
export function useSendMessage(projectId: string, conversationId: string | null, mode: ChatMode = "kb") {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (content: string) => {
      if (!conversationId) throw new Error("No conversation selected")
      return sendMessage(projectId, conversationId, content, token, mode)
    },
    onSuccess: async () => {
      // Refetch conversation history to get the new messages (including assistant response)
      // Use refetchQueries to wait for the refetch to complete
      await queryClient.refetchQueries({
        queryKey: ["conversations", projectId, conversationId, "history"],
      })
      // Also invalidate the conversations list
      queryClient.invalidateQueries({
        queryKey: ["conversations", projectId],
      })
    },
  })
}

